/**
 * Unified color utility functions
 *
 * Includes:
 * - Color format conversions (RGB, HSL, Hex)
 * - Brightness and lightness adjustments
 * - Color wheel calculations
 * - Theme color utilities
 *
 * Note: For color definitions, see @/styles/colors.ts
 */

import { semanticColors, greyScale } from "@/styles/colors";

// ============================================================================
// Color Format Conversions
// ============================================================================

/**
 * Convert RGB values to hex string
 */
export const rgbToHex = (r: number, g: number, b: number): string => {
  return (
    "#" +
    [r, g, b]
      .map((x) => {
        const hex = Math.round(x).toString(16);
        return hex.length === 1 ? "0" + hex : hex;
      })
      .join("")
  );
};

/**
 * Convert hex color to RGB values
 */
export const hexToRgb = (
  hex: string
): { r: number; g: number; b: number } | null => {
  if (!hex || !hex.startsWith("#")) return null;

  const cleanHex = hex.replace("#", "");
  if (cleanHex.length !== 6) return null;

  return {
    r: parseInt(cleanHex.substring(0, 2), 16),
    g: parseInt(cleanHex.substring(2, 4), 16),
    b: parseInt(cleanHex.substring(4, 6), 16),
  };
};

/**
 * Convert hex color to RGB string format "r, g, b" (for use in rgba())
 */
export const hexToRgbString = (hex: string): string => {
  const rgb = hexToRgb(hex);
  if (!rgb) return "0, 0, 0";
  return `${rgb.r}, ${rgb.g}, ${rgb.b}`;
};

/**
 * Convert hex color to HSL values
 */
export const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return { h: h * 360, s: s * 100, l: l * 100 };
};

/**
 * Convert HSL values to hex string
 */
export const hslToHex = (h: number, s: number, l: number): string => {
  const hNorm = h / 360;
  const sNorm = s / 100;
  const lNorm = l / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = lNorm < 0.5 ? lNorm * (1 + sNorm) : lNorm + sNorm - lNorm * sNorm;
  const p = 2 * lNorm - q;

  const r = Math.round(hue2rgb(p, q, hNorm + 1 / 3) * 255);
  const g = Math.round(hue2rgb(p, q, hNorm) * 255);
  const b = Math.round(hue2rgb(p, q, hNorm - 1 / 3) * 255);

  return rgbToHex(r, g, b);
};

// ============================================================================
// Color Adjustments
// ============================================================================

/**
 * Adjusts the brightness of a hex color
 * @param color - Hex color string (e.g., "#ff0000")
 * @param amount - Amount to adjust (-1 to 1, negative = darker, positive = lighter)
 */
export const adjustColorBrightness = (color: string, amount: number): string => {
  const DEFAULT_COLOR = greyScale[200];

  if (!color || !color.startsWith("#")) {
    return color || DEFAULT_COLOR;
  }

  const rgb = hexToRgb(color);
  if (!rgb) return DEFAULT_COLOR;

  const newR = Math.max(0, Math.min(255, Math.round(rgb.r * (1 + amount))));
  const newG = Math.max(0, Math.min(255, Math.round(rgb.g * (1 + amount))));
  const newB = Math.max(0, Math.min(255, Math.round(rgb.b * (1 + amount))));

  return rgbToHex(newR, newG, newB);
};

/**
 * Adjusts the lightness of a color by a percentage
 * @param color - Hex color string
 * @param percent - Percentage to adjust (-100 to 100)
 */
export const adjustLightness = (color: string, percent: number): string => {
  if (!color || !color.startsWith("#")) return color;

  const hsl = hexToHsl(color);
  const newL = Math.max(0, Math.min(100, hsl.l + percent));

  return hslToHex(hsl.h, hsl.s, newL);
};

// ============================================================================
// Color Analysis
// ============================================================================

/**
 * Determines if a color is light or dark
 * @param color - Hex color string
 * @returns true if the color is considered light
 */
export const isLightColor = (color: string): boolean => {
  if (!color || !color.startsWith("#")) {
    return true;
  }

  const rgb = hexToRgb(color);
  if (!rgb) return true;

  // Using luminance formula
  const luminance = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return luminance > 0.5;
};

/**
 * Normalizes a color string for comparison (lowercase, trimmed)
 */
export const normalizeColor = (color: string): string => {
  return color?.toLowerCase().trim() || "";
};

/**
 * Get the contrast color (black or white) for a given background color
 */
export const getContrastColor = (bgColor: string): string => {
  return isLightColor(bgColor) ? "#000000" : "#ffffff";
};

// ============================================================================
// Color Wheel
// ============================================================================

/**
 * Get color from position on a color wheel (0-100 for x and y)
 * Center is black, edges are colored based on angle
 */
export const getColorFromPosition = (x: number, y: number): string => {
  const centerX = 50;
  const centerY = 50;

  const angle = Math.atan2(y - centerY, x - centerX);
  const hue = ((angle + Math.PI) / (2 * Math.PI)) * 360;

  const distance = Math.sqrt(
    Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2)
  );
  const maxDistance = 50;
  const normalizedDistance = Math.min(distance / maxDistance, 1);

  const lightness = normalizedDistance * 85;
  const saturation = normalizedDistance * 80;

  return hslToHex(hue, saturation, lightness);
};

// ============================================================================
// Theme Color Utilities
// ============================================================================

/**
 * Gets a darker variant of a theme color
 * Uses the centralized color definitions from @/styles/colors.ts
 *
 * @param color - Color to get variant for
 * @returns Darker variant or original with brightness adjustment
 */
export const getThemeColorVariant = (color: string): string => {
  if (!color) return greyScale[200];

  const normalizedColor = normalizeColor(color);

  // Mapping of semantic colors to their darker variants
  const themeColorMap: Record<string, string> = {
    // Success
    [normalizeColor(semanticColors.success.main)]: semanticColors.success.dark,
    [normalizeColor(semanticColors.success.light)]: semanticColors.success.main,
    // Error
    [normalizeColor(semanticColors.error.main)]: semanticColors.error.dark,
    [normalizeColor(semanticColors.error.light)]: semanticColors.error.main,
    // Warning
    [normalizeColor(semanticColors.warning.main)]: semanticColors.warning.dark,
    [normalizeColor(semanticColors.warning.light)]: semanticColors.warning.main,
    // Info
    [normalizeColor(semanticColors.info.main)]: semanticColors.info.dark,
    [normalizeColor(semanticColors.info.light)]: semanticColors.info.main,
    // Orange
    [normalizeColor(semanticColors.orange.main)]: semanticColors.orange.dark,
    [normalizeColor(semanticColors.orange.light)]: semanticColors.orange.main,
    // Grey scale
    [normalizeColor(greyScale[600])]: greyScale[700],
    [normalizeColor(greyScale[50])]: greyScale[100],
    [normalizeColor(greyScale[100])]: greyScale[200],
    [normalizeColor(greyScale[200])]: greyScale[300],
    [normalizeColor(greyScale[300])]: greyScale[400],
  };

  // If it's a theme color, return its darker variant
  if (themeColorMap[normalizedColor]) {
    return themeColorMap[normalizedColor];
  }

  // Otherwise, use brightness adjustment
  return adjustColorBrightness(color, -0.15);
};
